<template>
    <div v-if="show">
        <simple-modal :show="show"
                      :show-title="false"
                      @close="close()">

            <portal-target name="savedReplyNotificationInputPortal" v-show="showPortalContent" @close="close()"></portal-target>
            <div v-show="!showPortalContent">
                <div class="p-2 flex flex-col gap-y-4" v-if="sharingSettings && !loading">
                    <slot name="title">
                        <div class="pt-2 flex justify-between items-center">
                            <h3 class="text-base font-medium">Share Online Inspection Report</h3>

                            <button class="text-gray-800 font-semibold outline-none hover:text-indigo-600 transition duration-300"
                                    @click="close" type="button">&#10005;
                            </button>
                        </div>
                    </slot>

                    <div class="flex flex-col w-full gap-y-4">
                        <div class="flex flex-wrap items-center gap-x-2 text-base font-medium">
                            {{ inspection.asset_name }}
                            <div class="text-indigo-600"> {{ inspection.date | dateShort }} {{ inspection.type | titlecase }} Inspection</div>
                        </div>

                        <div v-if="sharingSettings.public_url" class="flex flex-col gap-y-4">
                            <div class="flex">
                                <input :value="sharingSettings.public_url.signed_url" readonly
                                       class="flex-1 py-2 px-3 font-normal text-gray-600 text-xs rounded-l-md bg-gray-100"/>

                                <button class="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-2 rounded-r-md font-medium transition duration-300"
                                        @click.prevent="copyPageUrl">
                                    <icon-copy class="w-4 h-4"></icon-copy>
                                </button>
                            </div>

                            <span class="block text-gray-700 font-normal leading-relaxed break-normal">
                                Keep in mind that
                                <strong>anyone</strong> with this link will be able to view this inspection, so use it responsibly. You can always turn it off if it gets into the wrong hands.
                            </span>

                            <div class="flex justify-center">
                                <button class="bg-indigo-600 hover:bg-indigo-700 text-white p-2 px-4 font-medium rounded-md mr-2 transition duration-300"
                                        @click="previewOnlineReport">
                                    Preview Online Report
                                </button>

                                <button class="text-red-600 hover:text-red-700 hover:bg-red-100 text-white p-2 px-4 font-medium rounded-md transition duration-300"
                                        @click.prevent="toggleDisableLinkModal()">
                                    {{ publicLinkForm.busy ? "Disabling..." : "Disable this link" }}
                                </button>
                            </div>
                        </div>

                        <div v-if="!sharingSettings.public_url" class="flex flex-col gap-y-2">
                            <span class="block text-gray-700 font-normal leading-relaxed break-normal">
                                Public sharing allows you to send a link to this online inspection report to
                                <strong>anyone</strong>. Anyone with this link will be able to digitally sign the inspection.
                            </span>

                            <button class="bg-indigo-600 hover:bg-indigo-700 text-white p-2 font-medium rounded-md mt-2 transition duration-300"
                                    @click="generatePublicLink">
                                {{ publicLinkForm.busy ? "Generating..." : "Generate Shareable Link" }}
                            </button>
                        </div>
                    </div>

                    <div class="border-t border-gray-200 min-w-full pt-6 pb-4" v-if="sharingSettings.public_url && sharingSettings.contacts.length > 0">
                        <div v-if="state.user.default_mailbox_id === null">
                            <p class="text-yellow-700 bg-yellow-100 rounded-md mb-4 p-1 px-2 break-normal">No default mailbox to send an email from. <a
                                class="text-indigo-400 outline-none" :href="'/users/profile/' + app.userId">Click here</a> to select a default mailbox</p>
                        </div>

                        <div v-else>
                            <div class="mb-2 flex flex-wrap items-center justify-between">
                                <div class="font-medium text-md mb-1">Communication Scenario</div>
                                <simple-dropdown :label="selectedCommunicationScenario ? selectedCommunicationScenario.name : 'Choose Scenario' | titlecase"
                                                 class="w-full sm:w-1/2">
                                    <template slot="buttonOptions">
                                        <simple-dropdown-option v-for="scenario in communicationScenarios" class="flex items-center"
                                                                :key="scenario.id + 'scenario'" :value="scenario"
                                                                @click="selectCommunicationScenario(scenario)">
                                            {{ scenario.name | titlecase }}
                                            <icon-chip v-if="scenario.is_system" class="pl-1 text-gray-400 h-2 w-2"></icon-chip>
                                        </simple-dropdown-option>
                                    </template>
                                </simple-dropdown>
                            </div>

                            <div v-for="(contactGroup, role) in groupedContacts">
                                <div class="text-indigo-600 font-medium flex gap-y-4 mt-4 text-base">{{ role | pluralizeOnlyS(contactGroup.length) | titlecase }}</div>

                                <ol class="flex justify-between py-1 pr-4 md:pr-2 overflow-y-auto overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100"
                                    style="max-height: 10rem" v-for="contact in contactGroup">
                                    <inspection-contact :contact="contact" :inspection="inspection"
                                                        :ref="'inspectionContact' + contact.id"
                                                        @show-portal-content="toggleEditPortal()"
                                                        :default-communication="defaultCommunication"
                                                        @report-sent="$emit('update-online-report-status', inspection.id, 'shared')">
                                    </inspection-contact>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="loading" class="flex justify-center text-lg items-center my-36">
                    <icon-refresh class="h-10 w-10 text-indigo-600 animate-spin"/>
                    Loading...
                </div>
            </div>
        </simple-modal>

        <confirmation-dialog :show="showDisableLinkConfirmationModal" v-if="showDisableLinkConfirmationModal"
                             @proceed="disablePublicLink()" @cancelled="toggleDisableLinkModal()"
                             title="Disable online report link" subtitle="Are you sure you want to disable this online report link?"
                             proceed-button-text="Disable" :show-icon="false">
            <template slot="body">
                <div class="mt-4 flex flex-col gap-y-4">
                    <badge class="bg-yellow-100 text-yellow-600" text-size="text-normal">
                        Disabling and enabling the link will generate a new link, which will need to be re-shared to contacts.
                    </badge>

                    <input type="text" class="form-control" v-model="confirmDisableText" placeholder="Type DISABLE to confirm">
                </div>
            </template>
        </confirmation-dialog>
    </div>
</template>

<script>
import InspectionContact from "./InspectionContact";
import CommunicationScenarioMixin from "../../mixins/communication-scenario-mixin";
import CopyToClipboard from "../../mixins/copy-to-clipboard";

export default {
    components: {InspectionContact},

    mixins: [
        CopyToClipboard,
        CommunicationScenarioMixin
    ],

    props: ["inspection", "show"],

    data () {
        return {
            sharingSettings: {
                public_url: {},
                contacts: []
            },
            publicLinkForm: new AppForm({}),
            loading: true,
            communicationScenarios: [],
            showPortalContent: false,
            showDisableLinkConfirmationModal: false,
            confirmDisableText: "",
        };
    },

    watch: {
        show () {
            this.initForm();
        }
    },

    mounted () {
        this.initForm();
    },

    computed: {
        groupedContacts () {
            if (!this.sharingSettings || !this.sharingSettings.contacts) {
                return [];
            } else {
                return _.groupBy(this.sharingSettings.contacts, "pivot.role");
            }
        }
    },

    methods: {
        initForm () {
            this.loading = true;
            this.fetchInspectionSharingData()
            .then(() => this.getCommunicationScenarios()
            .then(() => this.loading = false
            )).catch(() =>
                this.loading = false
            );
        },

        fetchInspectionSharingData () {
            return new Promise((resolve, reject) => {
                axios.get("/inspections/" + this.inspection.id + "/sharing/settings")
                .then(response => {
                    this.sharingSettings = response.data;
                    resolve();
                })
                .catch(error => {
                    reject();
                    toastr.error("Error fetching contacts. Error: " + error.message);
                });
            });
        },

        generatePublicLink () {
            App.post("/inspections/" + this.inspection.id + "/sharing/generate-link", this.publicLinkForm)
            .then(response => {
                this.sharingSettings = response;
                this.$nextTick(function () {
                    // make sure the system comm scenario gets auto selected
                    this.getCommunicationScenarios();
                    this.$emit("update-online-report-status", this.inspection.id, "generated");
                });
            })
            .catch(error => {
                toastr.error("Error generating online report link: " + error.message);
            });
        },

        disablePublicLink () {
            if (this.confirmDisableText !== "DISABLE") {
                toastr.error("You must type DISABLE to confirm disabling the link");
                return;
            }

            this.toggleDisableLinkModal(false);
            App.post("/inspections/" + this.inspection.id + "/sharing/disable-link", this.publicLinkForm)
            .then(response => {
                this.sharingSettings = response;
                this.$emit("update-online-report-status", this.inspection.id, "not generated");
            })
            .catch(error => {
                toastr.error("Error disabling online report link: " + error.message);
            });
        },

        toggleDisableLinkModal (override = null) {
            if (override) {
                this.showDisableLinkConfirmationModal = override;
                return;
            }

            this.showDisableLinkConfirmationModal = !this.showDisableLinkConfirmationModal;
        },

        previewOnlineReport () {
            window.open(this.sharingSettings.public_url.signed_url);
        },

        copyPageUrl () {
            this.copyToClipboard(this.sharingSettings.public_url.signed_url);
            toastr.success("Link Copied!");
        },

        getCommunicationScenarios () {
            return new Promise((resolve, reject) => {
                this.preSelectedScenarioName = "sharing online inspection report";
                axios.get("/communication-scenarios/search", {
                    params: {
                        scenario: ["sharing online inspection report"]
                    }
                })
                .then(response => {
                    this.communicationScenarios = response.data;
                    resolve();
                })
                .catch(error => {
                    reject();
                    toastr.error(error.message);
                });
            });
        },

        selectCommunicationScenario (scenario) {
            this.selectedCommunicationScenario = scenario;

            if (this.selectedCommunicationScenario && this.selectedCommunicationScenario.saved_replies) {
                this.defaultCommunication = this.getSavedReplies(scenario);
            }

            // use next tick to allow the refs to properly load due to the v-if on this.loading
            this.$nextTick(function () {
                Object.values(this.$refs).forEach(ref => {
                    if (ref[0].$vnode.tag.includes("InspectionContact")) {
                        ref[0].setContactCommunication();
                    }
                });
            });
        },

        close () {
            this.$emit("close");
        },

        toggleEditPortal () {
            this.showPortalContent = !this.showPortalContent;
        }
    },
};
</script>